import {
  Badge,
  BodyText,
  Button,
  Card,
  Heading,
  Icon,
  IconSet,
} from '@postscript/components';
import styled from 'styled-components';
import { BadgeVariant } from '../types';

const StyledBox = styled.div`
  border: 1px solid var(--border-color-dim);
  border-radius: var(--border-radius-medium);
  display: flex;
  flex-direction: column;
  padding: var(--spacing-2) var(--spacing-4) var(--spacing-3) var(--spacing-4);
  & > button {
    margin-top: var(--spacing-2);
    width: fit-content;
  }
`;

const StyledDecorations = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const StyledHeading = styled(Heading)`
  margin: calc(var(--spacing-1) / 2) 0;
`;

interface BoxProps {
  cta: {
    dataCy: string;
    handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    label: string;
    variation?: 'primary' | 'secondary' | 'plain' | 'text'; // ButtonProps
  };
  description: string;
  icon: () => JSX.Element;
  tag?: {
    text: string;
    variant: BadgeVariant;
  };
  title: string;
}

export default function CtaBox(props: BoxProps) {
  const { cta, description, icon, tag, title } = props;
  const { dataCy, handleClick, label, variation } = cta;
  return (
    <StyledBox>
      <div>
        <StyledDecorations>
          <Icon component={icon} size={36} color="var(--highlight-color)" />
          {tag && (
            <Badge size="small" variant={tag.variant}>
              {tag.text}
            </Badge>
          )}
        </StyledDecorations>
        <StyledHeading color="var(--black)" forwardedAs="h4" size="xx-small">
          {title}
        </StyledHeading>
        <BodyText color="var(--text-color-dim)" size="small">
          {description}
        </BodyText>
      </div>
      <Button
        data-cy={dataCy}
        variant={variation || 'secondary'}
        size="small"
        onClick={handleClick}
      >
        <BodyText as="span" size="x-small" bold color="var(--button-color)">
          {label}
        </BodyText>
      </Button>
    </StyledBox>
  );
}

interface StyledButtonProps {
  disabled?: boolean;
}

const StyledButton = styled(Card)<StyledButtonProps>`
  align-items: flex-start;
  border: 1px solid var(--border-color-dim);
  border-radius: var(--border-radius-medium);
  cursor: pointer;
  display: flex;
  gap: var(--spacing-1);
  padding: var(--spacing-2) var(--spacing-3) var(--spacing-2) var(--spacing-2);
  transition: var(--hover-transition);

  svg {
    transition: var(--hover-transition);
  }

  &:hover, &:focus {
    border: 1px solid var(--highlight-color);
  }

  &:hover svg, &:focus svg {
    color: var(--highlight-color);
  }

${({ disabled }) =>
  disabled &&
  `
    opacity: 0.5;
    pointer-events: none;
  `}
}
`;

const StyledInnerWrapper = styled.span`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding: var(--spacing-1) 0;
  width: 100%;
`;

const StyledArrowIcon = styled(Icon)`
  margin-top: calc(var(--spacing-1) / 2);
`;

export const CtaButton = ({
  description,
  disabled,
  icon,
  onClick,
  title,
}: {
  description: string;
  disabled?: boolean;
  icon?: () => JSX.Element;
  onClick: () => void;
  title: string;
}) => {
  return (
    <StyledButton
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      size="small"
    >
      {icon && <Icon component={icon} color="var(--highlight-color)" />}
      <StyledInnerWrapper>
        <Heading as="h4" size="xx-small">
          {title}
        </Heading>
        <BodyText size="small">{description}</BodyText>
      </StyledInnerWrapper>
      <StyledArrowIcon
        component={IconSet.ArrowRight}
        color="var(--border-color-dim)"
        size="large"
      />
    </StyledButton>
  );
};
